import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export type SortDir = 'asc' | 'desc'

export interface DataTableColumnFilter {
  id: string
  label: string
  /** Distinct option values derived from rows when omitted. */
  options?: { value: string; label: string }[]
}

export interface DataTableColumn<T> {
  id: string
  header: string
  align?: 'start' | 'end' | 'center'
  sortable?: boolean
  /** Default true for numeric financial cells. */
  mono?: boolean
  width?: string
  filterable?: boolean
  accessor?: (row: T) => string | number | null | undefined
  cell?: (row: T, index: number) => ReactNode
  /** Flash cell when this value changes between renders. */
  flashKey?: (row: T) => string | number | null | undefined
  /** Value used for column filter matching. */
  filterValue?: (row: T) => string
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T, index: number) => string
  searchable?: boolean
  searchPlaceholder?: string
  searchFilter?: (row: T, query: string) => boolean
  /** Enable CSV download of the filtered/sorted dataset. */
  exportable?: boolean
  exportFilename?: string
  pageSize?: number
  pageSizeOptions?: number[]
  className?: string
  emptyMessage?: string
  dense?: boolean
  title?: string
}

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100]

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function escapeCsv(value: unknown): string {
  const s = value == null ? '' : String(value)
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

/**
 * High-density institutional DataTable:
 * sticky header, toolbar (search / filters / CSV), sort, pagination,
 * row-count selector, tabular nums, real-time price flash.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  searchable = true,
  searchPlaceholder,
  searchFilter,
  exportable = true,
  exportFilename = 'export.csv',
  pageSize: initialPageSize = 25,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  className,
  emptyMessage,
  dense = true,
  title,
}: DataTableProps<T>) {
  const { t } = useTranslation('common')
  const [query, setQuery] = useState('')
  const [sortId, setSortId] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  )
  const [flashMap, setFlashMap] = useState<Record<string, 'up' | 'down'>>({})
  const prevFlashRef = useRef(new Map<string, string | number>())

  const filterableColumns = useMemo(
    () => columns.filter((c) => c.filterable),
    [columns],
  )

  const filterOptions = useMemo(() => {
    const map: Record<string, { value: string; label: string }[]> = {}
    filterableColumns.forEach((col) => {
      const values = new Set<string>()
      rows.forEach((row) => {
        const raw =
          col.filterValue?.(row) ??
          (col.accessor?.(row) != null ? String(col.accessor(row)) : '')
        if (raw) values.add(raw)
      })
      map[col.id] = [...values].sort().map((v) => ({ value: v, label: v }))
    })
    return map
  }, [filterableColumns, rows])

  const filtered = useMemo(() => {
    let next = rows

    const q = query.trim().toLowerCase()
    if (q) {
      next = searchFilter
        ? next.filter((r) => searchFilter(r, q))
        : next.filter((row) =>
            columns.some((col) => {
              const val = col.accessor?.(row)
              return val != null && String(val).toLowerCase().includes(q)
            }),
          )
    }

    Object.entries(columnFilters).forEach(([colId, value]) => {
      if (!value) return
      const col = columns.find((c) => c.id === colId)
      if (!col) return
      next = next.filter((row) => {
        const raw =
          col.filterValue?.(row) ??
          (col.accessor?.(row) != null ? String(col.accessor(row)) : '')
        return raw === value
      })
    })

    return next
  }, [rows, query, columns, searchFilter, columnFilters])

  const sorted = useMemo(() => {
    if (!sortId) return filtered
    const col = columns.find((c) => c.id === sortId)
    if (!col?.accessor) return filtered
    const copy = [...filtered]
    copy.sort((a, b) => {
      const av = col.accessor!(a)
      const bv = col.accessor!(b)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av))
    })
    return copy
  }, [filtered, sortId, sortDir, columns])

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const safePage = Math.min(page, pageCount - 1)
  const pageRows = sorted.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  )

  useEffect(() => {
    const nextFlash: Record<string, 'up' | 'down'> = {}
    rows.forEach((row, index) => {
      const key = rowKey(row, index)
      columns.forEach((col) => {
        if (!col.flashKey) return
        const cellKey = `${key}:${col.id}`
        const current = col.flashKey(row)
        if (current == null) return
        const prev = prevFlashRef.current.get(cellKey)
        if (prev != null && prev !== current) {
          nextFlash[cellKey] =
            typeof current === 'number' && typeof prev === 'number'
              ? current > prev
                ? 'up'
                : 'down'
              : 'up'
        }
        prevFlashRef.current.set(cellKey, current)
      })
    })

    if (Object.keys(nextFlash).length === 0) return

    setFlashMap((prev) => ({ ...prev, ...nextFlash }))
    const timer = window.setTimeout(() => {
      setFlashMap((prev) => {
        const clone = { ...prev }
        Object.keys(nextFlash).forEach((k) => {
          delete clone[k]
        })
        return clone
      })
    }, 520)
    return () => window.clearTimeout(timer)
  }, [rows, columns, rowKey])

  const toggleSort = (colId: string) => {
    if (sortId === colId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortId(colId)
      setSortDir('asc')
    }
    setPage(0)
  }

  const exportCsv = () => {
    const header = columns.map((c) => escapeCsv(c.header)).join(',')
    const body = sorted
      .map((row) =>
        columns
          .map((col) => escapeCsv(col.accessor?.(row) ?? ''))
          .join(','),
      )
      .join('\n')
    downloadCsv(exportFilename, `${header}\n${body}`)
  }

  const controlClass = cn(
    'h-7 rounded-sm border border-terminal-border/40 bg-terminal-bg px-2 text-[11px]',
    'text-terminal-fg placeholder:text-terminal-muted',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
  )

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col rounded-sm border border-terminal-border/40 bg-terminal-surface',
        className,
      )}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-terminal-border/40 px-2 py-1.5">
        {title ? (
          <h2 className="me-1 text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
            {title}
          </h2>
        ) : null}

        {searchable ? (
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setPage(0)
            }}
            placeholder={searchPlaceholder ?? t('actions.search')}
            className={cn(controlClass, 'w-36 max-w-full sm:w-48')}
          />
        ) : null}

        {filterableColumns.map((col) => (
          <select
            key={col.id}
            value={columnFilters[col.id] ?? ''}
            onChange={(e) => {
              setColumnFilters((prev) => ({
                ...prev,
                [col.id]: e.target.value,
              }))
              setPage(0)
            }}
            className={cn(controlClass, 'max-w-[9rem]')}
            aria-label={col.header}
          >
            <option value="">
              {t('table.allFilter', { column: col.header })}
            </option>
            {(filterOptions[col.id] ?? []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ))}

        <div className="ms-auto flex items-center gap-1.5">
          <span className="font-mono text-[10px] tabular-nums text-terminal-muted">
            {filtered.length}
          </span>
          {exportable ? (
            <button
              type="button"
              onClick={exportCsv}
              className={cn(
                'h-7 rounded-sm border border-terminal-border/40 px-2 text-[11px]',
                'text-terminal-muted hover:bg-terminal-elevated hover:text-terminal-fg',
              )}
            >
              {t('table.exportCsv')}
            </button>
          ) : null}
        </div>
      </div>

      {/* Grid */}
      <div className="terminal-scroll min-h-0 flex-1 overflow-auto">
        <table className="w-full table-fixed border-collapse text-[11px]">
          <thead className="sticky top-0 z-10 bg-terminal-surface">
            <tr className="border-b border-terminal-border/40">
              {columns.map((col) => {
                const align =
                  col.align === 'end'
                    ? 'text-end'
                    : col.align === 'center'
                      ? 'text-center'
                      : 'text-start'
                return (
                  <th
                    key={col.id}
                    style={col.width ? { width: col.width } : undefined}
                    className={cn(
                      'px-3 font-medium uppercase tracking-wider text-terminal-muted',
                      dense ? 'py-1.5' : 'py-2',
                      align,
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.id)}
                        className="inline-flex items-center gap-1 hover:text-terminal-fg"
                      >
                        {col.header}
                        <span
                          className={cn(
                            'font-mono text-[9px]',
                            sortId === col.id
                              ? 'text-accent'
                              : 'text-terminal-border',
                          )}
                        >
                          {sortId === col.id
                            ? sortDir === 'asc'
                              ? '▲'
                              : '▼'
                            : '⇅'}
                        </span>
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-terminal-muted"
                >
                  {emptyMessage ?? t('table.empty')}
                </td>
              </tr>
            ) : (
              pageRows.map((row, index) => {
                const key = rowKey(row, index)
                return (
                  <tr
                    key={key}
                    className="border-b border-terminal-border/40 transition-colors hover:bg-terminal-elevated/50"
                  >
                    {columns.map((col) => {
                      const cellKey = `${key}:${col.id}`
                      const flash = flashMap[cellKey]
                      const align =
                        col.align === 'end'
                          ? 'text-end'
                          : col.align === 'center'
                            ? 'text-center'
                            : 'text-start'
                      return (
                        <td
                          key={col.id}
                          className={cn(
                            'px-3',
                            dense ? 'py-1.5' : 'py-2',
                            align,
                            col.mono !== false &&
                              'font-mono tabular-nums tracking-tight',
                            flash === 'up' &&
                              'animate-[flash-up_520ms_ease-out]',
                            flash === 'down' &&
                              'animate-[flash-down_520ms_ease-out]',
                          )}
                        >
                          {col.cell
                            ? col.cell(row, index)
                            : (col.accessor?.(row) ?? '—')}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-terminal-border/40 px-2 py-1.5 text-[11px] text-terminal-muted">
        <div className="flex items-center gap-1.5">
          <span>{t('table.rows')}</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value))
              setPage(0)
            }}
            className={controlClass}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={safePage <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="h-7 rounded-sm px-2 hover:bg-terminal-elevated disabled:opacity-40"
          >
            {t('table.prev')}
          </button>
          <span className="min-w-16 text-center font-mono tabular-nums">
            {safePage + 1} / {pageCount}
          </span>
          <button
            type="button"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="h-7 rounded-sm px-2 hover:bg-terminal-elevated disabled:opacity-40"
          >
            {t('table.next')}
          </button>
        </div>
      </div>
    </div>
  )
}
